import { useAuth } from "./useAuth";

import { UserRole } from "@/types/user/userType";

export enum Action {
  VIEW,
  EDIT,
  DELETE,
  CREATE,
}

// Role hierarchy: lower number = higher authority
const ROLE_LEVELS = {
  [UserRole.SUPER_ADMIN]: 0,
  [UserRole.ADMIN]: 1,
  [UserRole.MANAGER]: 2,
  [UserRole.SPECTATOR]: 3,
} as const;

// Simple permission matrix: minimum role level required
const PERMISSIONS = {
  users: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.EDIT]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.DELETE]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.VIEW]: ROLE_LEVELS[UserRole.SPECTATOR],
  },
  segments: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.EDIT]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.DELETE]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.VIEW]: ROLE_LEVELS[UserRole.SPECTATOR],
  },
  clients: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.EDIT]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.DELETE]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.VIEW]: ROLE_LEVELS[UserRole.SPECTATOR],
  },
  campaigns: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.EDIT]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.DELETE]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.VIEW]: ROLE_LEVELS[UserRole.SPECTATOR],
  },
  jobBoards: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.EDIT]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.DELETE]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.VIEW]: ROLE_LEVELS[UserRole.SPECTATOR],
  },
  vacancies: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.SUPER_ADMIN],
    [Action.EDIT]: ROLE_LEVELS[UserRole.SUPER_ADMIN],
    [Action.DELETE]: ROLE_LEVELS[UserRole.MANAGER],
    [Action.VIEW]: ROLE_LEVELS[UserRole.SPECTATOR],
  },
  insights: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.SUPER_ADMIN],
    [Action.EDIT]: ROLE_LEVELS[UserRole.SUPER_ADMIN],
    [Action.DELETE]: ROLE_LEVELS[UserRole.SUPER_ADMIN],
    [Action.VIEW]: ROLE_LEVELS[UserRole.SPECTATOR],
  },
  spent: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.EDIT]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.DELETE]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.VIEW]: ROLE_LEVELS[UserRole.SPECTATOR],
  },
  integrations: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.SUPER_ADMIN],
    [Action.EDIT]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.DELETE]: ROLE_LEVELS[UserRole.SUPER_ADMIN],
    [Action.VIEW]: ROLE_LEVELS[UserRole.ADMIN],
  },
  captain: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.EDIT]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.DELETE]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.VIEW]: ROLE_LEVELS[UserRole.SPECTATOR],
  },
  captainBilling: {
    [Action.CREATE]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.EDIT]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.DELETE]: ROLE_LEVELS[UserRole.ADMIN],
    [Action.VIEW]: ROLE_LEVELS[UserRole.MANAGER],
  },
} as const;

export type EntityName = keyof typeof PERMISSIONS;

export const usePermissionMatrix = () => {
  const { role } = useAuth();

  const can = (entity: EntityName, action: Action): boolean => {
    if (!role) return false;

    const currentLevel = ROLE_LEVELS[role];
    const requiredLevel = PERMISSIONS[entity][action];

    if (currentLevel === undefined || requiredLevel === undefined) return false;
    if (currentLevel > requiredLevel) return false;

    return true;
  };

  const canManageRole = (targetRole: string) => {
    if (!role) return false;
    const currentLevel = ROLE_LEVELS[role];
    const targetLevel = ROLE_LEVELS[targetRole as UserRole];

    if (currentLevel === undefined || targetLevel === undefined) return false;

    // SuperAdmin can manage same level; all others can only manage lower levels
    if (currentLevel === ROLE_LEVELS[UserRole.SUPER_ADMIN]) {
      return currentLevel <= targetLevel;
    }
    return currentLevel < targetLevel;
  }

  const canDeleteRole = (targetRole: string) => {
    if (!role) return false;
    const currentLevel = ROLE_LEVELS[role];
    const targetLevel = ROLE_LEVELS[targetRole as UserRole];

    if (currentLevel === undefined || targetLevel === undefined) return false;

    return currentLevel < targetLevel; // Can only delete lower level users, not same level
  }

  return {
    can,
    canView: (entity: EntityName) => can(entity, Action.VIEW),
    canEdit: (entity: EntityName) => can(entity, Action.EDIT),
    canDelete: (entity: EntityName) => can(entity, Action.DELETE),
    canCreate: (entity: EntityName) => can(entity, Action.CREATE),

    canManageRole,
    canDeleteRole,
  };
};
